/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	CheckCircle,
	Ruler,
	Palette,
	Smile,
	AlertTriangle,
	MessageSquare,
	ShieldCheck,
} from "lucide-react"; // Refined icons
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path

// --- Structured Guarantee Information ---
const guaranteeData = {
	intro: {
		title: "Your Satisfaction, Guaranteed",
		text: "Because every shade is custom-made just for you, traditional returns aren't possible. However, your satisfaction is our priority! We offer these guarantees to ensure you love your new shades.",
	},
	guarantees: [
		{
			id: "fit",
			icon: Ruler,
			title: "SureFit™ Measurement Guarantee", // Give it a name
			// Focus on the *solution* for the customer
			description:
				"Mistakes happen! If you accidentally mis-measure, contact us within **14 days** of receiving your order. We'll work with you on the best solution, which often includes remaking your shade at a significant discount or adjusting it at no cost.",
			actionText: "Contact us about a measurement issue",
			contactLink: "/contact", // Or specific link/email
		},
		{
			id: "quality",
			icon: ShieldCheck, // Use warranty icon
			title: "Quality & Manufacturing Guarantee", // Combine Fit + Quality Defect
			description:
				"We meticulously inspect every shade. In the rare case your shade arrives damaged or doesn't match the dimensions you provided due to a manufacturing defect, contact us right away. We'll repair or replace it free of charge.",
			actionText: "Report a defect or damage",
			contactLink: "/contact", // Or specific link/email
		},
		{
			id: "satisfaction",
			icon: Smile, // Changed from Heart
			title: "Satisfaction Guarantee",
			description:
				"Not 100% happy with the quality, style, or overall look? Let us know within **14 days** of receiving your order. We're committed to working with you to find a solution and make it right.",
			actionText: "Discuss satisfaction concerns",
			contactLink: "/contact",
		},
		{
			id: "color",
			icon: Palette,
			title: "Color Accuracy (Order Samples!)",
			// Explain *why* samples are important
			description:
				"Screen colors vary! While we strive for accuracy online, the best way to ensure a perfect match is to order **free fabric samples** before you buy. We cannot accept returns based on color discrepancies if samples weren't ordered.",
			actionText: "Order Free Fabric Samples",
			link: "/swatches", // Link to samples page
		},
	],
	note: {
		icon: AlertTriangle,
		title: "Important Considerations",
		text: (
			<>
				This guarantee covers the original purchaser and installation only. It
				does not cover issues from misuse, alterations, accidents, or normal
				wear and tear. Please see our full{" "}
				<Link
					to="/help/warranty"
					className="text-primary hover:underline font-medium"
				>
					5-Year Warranty
				</Link>{" "}
				for details on long-term defect coverage.
			</>
		), // Link to full warranty
	},
};

// --- Main Guarantees Screen Component ---
const GuaranteesScreen = () => {
	// Renamed component

	return (
		<div className="overflow-x-hidden bg-gray-50">
			{" "}
			{/* Standard bg */}
			<Helmet>
				<title>
					Our Guarantees: SureFit™, Quality & Satisfaction | Off Cut Shades
				</title>{" "}
				{/* Updated Title */}
				<meta
					name="description"
					content="Shop with confidence! Learn about Off Cut Shades' SureFit™ Measurement Guarantee, Quality Guarantee, and Satisfaction Guarantee for your custom window shades."
				/>{" "}
				{/* Updated Desc */}
				{/* Changed canonical URL to reflect focus on guarantees */}
				<link rel="canonical" href="https://offcutshades.com/help/guarantee" />
				{/* Keep the old return policy URL redirecting here if needed */}
			</Helmet>
			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<CheckCircle size={36} strokeWidth={1.5} /> {/* Main Icon */}
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Shop With Confidence
					</motion.h1>
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="text-lg text-gray-600 max-w-2xl mx-auto"
					>
						{guaranteeData.intro.text} {/* Use intro text from data */}
					</motion.p>
				</section>
				{/* --- Guarantees Grid --- */}
				<section className="container mx-auto px-4 max-w-4xl mx-auto">
					{/* Use grid with fewer columns for better readability */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
						{guaranteeData.guarantees.map((guarantee, index) => (
							<motion.div
								key={guarantee.id}
								className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col h-full" // Use flex-col
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.5, delay: index * 0.1 }}
							>
								{/* Icon & Title */}
								<div className="flex items-center gap-3 mb-3">
									<div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
										{React.createElement(guarantee.icon, {
											size: 24,
											strokeWidth: 2,
										})}
									</div>
									<h2 className="text-xl font-semibold text-gray-800">
										{guarantee.title}
									</h2>
								</div>
								{/* Description */}
								<p
									className="text-sm text-gray-700 flex-grow mb-4"
									dangerouslySetInnerHTML={{
										__html: guarantee.description.replace(
											/\*\*(.*?)\*\*/g,
											"<strong>$1</strong>"
										),
									}}
								>
									{/* Use dangerouslySetInnerHTML to render bold text */}
								</p>
								{/* Action Button/Link */}
								{guarantee.link ? (
									<Link
										to={guarantee.link}
										className="mt-auto inline-block text-center btn btn-secondary-outline text-sm py-2"
									>
										{guarantee.actionText}
									</Link>
								) : (
									guarantee.contactLink && (
										<Link
											to={guarantee.contactLink}
											className="mt-auto inline-block text-center btn btn-secondary-outline text-sm py-2"
										>
											{guarantee.actionText}
										</Link>
									)
								)}
							</motion.div>
						))}
					</div>

					{/* Important Note / Link to Full Warranty */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="mt-10 md:mt-12 bg-yellow-50 border border-yellow-200 text-yellow-800 p-5 rounded-lg text-sm flex items-start gap-3"
					>
						<guaranteeData.note.icon
							size={20}
							className="mt-0.5 text-yellow-600 flex-shrink-0"
						/>
						<div>
							<h3 className="font-semibold mb-1">{guaranteeData.note.title}</h3>
							<p>{guaranteeData.note.text}</p>
						</div>
					</motion.div>
				</section>
			</main>
			{/* --- Contact Block --- */}
			<ContactBlock
				title="Have Questions About Our Guarantees?"
				description="We're here to help ensure you're completely happy with your purchase. Contact us for any clarifications."
			/>
		</div>
	);
};

export default GuaranteesScreen; // Renamed export
